/**
 * THE FILE — one piece of work, read the way a file is read.
 *
 * The spine names its sections (Work · Evidence · Workpaper · History) and
 * carries the professional position broken out, so the position is never off
 * screen. The file itself is the reading column. The dock, on a wide desk,
 * keeps the other thing you would have open beside it — the workpaper while
 * you read the evidence, the evidence while you read the workpaper — and it
 * simply does not exist when there is nothing to put in it.
 *
 * Sections are pages of one file, not tabs: choosing one returns to the top,
 * because that is what turning a page does.
 *
 * ONE ANSWER, ALWAYS. Every act on this screen returns the whole work file as
 * the server now projects it, and the screen replaces what it holds with that.
 * It never patches its copy: a professional state the browser edited would be
 * a second answer to a question the record has already answered once.
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { WorkFileResponse, type WorkFile } from "@leveri/contracts";
import {
  Dock, FileLayout, Label, Notice, Reading, RecordName, Spine, SpineGroup, SpineItem, SpineSub, StateLedger, SERIF_OPSZ } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { EvidenceSection } from "./evidence.js";
import { HistorySection } from "./history.js";
import { WorkSection } from "./workSection.js";
import { WorkpaperSection } from "./workpaper.js";

/** How often a WORKING file re-reads itself. */
const REFRESH_WHILE_WORKING_MS = 2000;

/** The four pages of a file. There is no fifth, and the spine names them in this order. */
type Section = "work" | "evidence" | "workpaper" | "history";

export function WorkFileScreen() {
  const { workId } = useParams<{ workId: string }>();
  const navigate = useNavigate();
  const [work, setWork] = useState<WorkFile | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [section, setSection] = useState<Section>("work");
  /** What the last act actually did, in the server's own words. */
  const [outcome, setOutcome] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workId) return;
    setFailure(null);
    try {
      const response = await call("GET", `/api/work/${workId}`, WorkFileResponse);
      setWork(response.work);
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, [workId]);

  useEffect(() => { void load(); }, [load]);

  /**
   * R11 — ARRIVING FROM THE WORKSPACE. A queue item names the section of the
   * file where its act is (#conclusions, #open, #bridge). Once the file has
   * rendered, that section is brought into view — once per arrival, so it
   * never fights the reader's own scrolling afterwards.
   */
  const { hash } = useLocation();
  const [arrivedAt, setArrivedAt] = useState<string | null>(null);
  useEffect(() => {
    if (!work || !hash || arrivedAt === `${work.id}${hash}`) return;
    setSection("work");
    const id = decodeURIComponent(hash.slice(1));
    const frame = window.requestAnimationFrame(() => {
      // An exact request card, else (it was answered meanwhile) the requests section.
      (document.getElementById(id) ?? (id.startsWith("request-") ? document.getElementById("open") : null))?.scrollIntoView({ block: "start" });
      setArrivedAt(`${work.id}${hash}`);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [work, hash, arrivedAt]);

  /**
   * WORKING IS OBSERVABLE (R6.5). While the server says the work is WORKING —
   * and no person is needed — the file re-reads itself, quietly, until the
   * server says something else. A refresh that fails keeps the last answer on
   * screen and tries again; it never blanks the page. Nothing here decides a
   * state: the next reading is simply the server's.
   */
  const working = work?.status === "WORKING" && !work.needsAttention;
  useEffect(() => {
    if (!working || !workId) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await call("GET", `/api/work/${workId}`, WorkFileResponse);
        if (!cancelled) setWork(response.work);
      } catch {
        if (!cancelled) setWork((current) => (current ? { ...current } : current));
      }
    }, REFRESH_WHILE_WORKING_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [working, workId, work]);

  const back = (
    <button
      type="button"
      onClick={() => navigate("/")}
      className="inline-flex items-center gap-1.5 text-[13px] text-stone transition-colors hover:text-ink"
    >
      <ArrowLeft size={14} /> Accounting work
    </button>
  );

  function open(next: Section) {
    setSection(next);
    window.scrollTo({ top: 0 });
  }

  // Stable: children use it in effect dependencies (the suggestions request, asked once per version).
  const restate = useCallback((next: WorkFile) => setWork(next), []);

  if (failure) {
    return (
      <FileLayout spine={<Spine>{back}</Spine>}>
        <Notice>{failure}</Notice>
        <button type="button" onClick={() => void load()} className="mt-3 text-[13px] text-stone hover:text-ink">
          Try again
        </button>
      </FileLayout>
    );
  }
  if (!work) {
    return (
      <FileLayout spine={<Spine>{back}</Spine>}>
        <Reading>Opening the file…</Reading>
      </FileLayout>
    );
  }

  const spine = (
    <Spine>
      {back}
      <SpineGroup label="In this file">
        <SpineItem
          current={section === "work"}
          onSelect={() => open("work")}
          sub={
            <>
              <SpineSub href="#figures">Figures</SpineSub>
              <SpineSub href="#established">Established</SpineSub>
              {work.openRequests.length > 0 && <SpineSub href="#open">Open · {work.openRequests.length}</SpineSub>}
            </>
          }
        >
          Work
        </SpineItem>
        <SpineItem current={section === "evidence"} onSelect={() => open("evidence")} count={work.records.length || null}>
          Evidence
        </SpineItem>
        <SpineItem current={section === "workpaper"} onSelect={() => open("workpaper")} count={work.workpapers.length || null}>
          Workpaper
        </SpineItem>
        <SpineItem current={section === "history"} onSelect={() => open("history")} count={work.history.length || null}>
          History
        </SpineItem>
      </SpineGroup>
      {/* THE STATE LEDGER — the position, broken out, and computed on the
          server. Always open on a wide desk, behind a disclosure below it. */}
      <div className="hidden lg:block">
        <Label className="mb-3">State</Label>
        <StateLedger rows={work.stateLedger} />
      </div>
      <details className="lg:hidden">
        <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-[0.09em] text-stone">State</summary>
        <div className="mt-3"><StateLedger rows={work.stateLedger} /></div>
      </details>
    </Spine>
  );

  // The dock holds the OTHER thing. With no workpaper there is nothing to
  // keep open beside the file, and the third column does not exist at all.
  const dock = work.workpapers.length === 0
    ? undefined
    : section === "workpaper"
      ? <EvidenceDock work={work} onOpen={() => open("evidence")} />
      : <WorkpaperDock work={work} onOpen={() => open("workpaper")} />;


  return (
    <FileLayout spine={spine} dock={dock}>
      {section === "work" && (
        <WorkSection work={work} outcome={outcome} onWork={restate} onOutcome={setOutcome} />
      )}
      {section === "evidence" && <EvidenceSection work={work} onWork={restate} />}
      {section === "workpaper" && <WorkpaperSection work={work} />}
      {section === "history" && <HistorySection work={work} />}
    </FileLayout>
  );
}

function WorkpaperDock({ work, onOpen }: { work: WorkFile; onOpen: () => void }) {
  const current = work.workpapers.find((version) => version.current) ?? work.workpapers[work.workpapers.length - 1];
  // THE DOCUMENT, NOT THE TITLE AGAIN (owner-accepted design, 2026-10-09): the
  // figures the workpaper carries and where its sign-off stands — all the server's.
  return (
    <Dock
      label={current ? `Workpaper · version ${current.versionSeq}` : "Workpaper"}
      actions={<button type="button" onClick={onOpen} className="font-medium text-forest hover:underline">Open the workpaper</button>}
    >
      <div className="border border-rule bg-raised px-4 py-3.5 text-[13px] [font-variant-numeric:tabular-nums]">
        <div className="font-serif text-[15px] text-ink" style={SERIF_OPSZ}>{work.engagementTitle}</div>
        <div className="mt-0.5 text-stone">{work.clientName}</div>
        {work.figures.length > 0 && (
          <div className="mt-3 flex flex-col gap-1.5 border-t border-rule-soft pt-3">
            {work.figures.map((figure) => (
              <div key={figure.key} className="min-w-0">
                <div className="text-[12px] text-stone">{figure.label}</div>
                <div className="break-words font-mono text-ink">{figure.text}</div>
              </div>
            ))}
          </div>
        )}
        {work.review.total > 0 && (
          <div className="mt-3 border-t border-rule-soft pt-3 text-ink-soft">
            Signed off: {work.review.signedOff} of {work.review.total}
          </div>
        )}
      </div>
      {work.workpapers.length > 1 && (
        <div className="mt-3 text-[12px] text-stone">
          {work.workpapers.length - 1} earlier {work.workpapers.length === 2 ? "version is" : "versions are"} kept under History.
        </div>
      )}
    </Dock>
  );
}

function EvidenceDock({ work, onOpen }: { work: WorkFile; onOpen: () => void }) {
  return (
    <Dock
      label="Evidence · docked"
      meta={`${work.records.length} enclosed`}
      actions={<button type="button" onClick={onOpen} className="font-medium text-forest hover:underline">Open evidence</button>}
    >
      <div className="flex flex-col gap-3 text-[13px]">
        {work.records.map((record) => (
          <div key={record.enclosureId} className="min-w-0">
            <RecordName className="block truncate text-[12px]">{record.filename}</RecordName>
            {record.roleLabel && <div className="text-stone">{record.roleLabel}</div>}
          </div>
        ))}
      </div>
    </Dock>
  );
}
